/**
 * Blocks non-numeric keys (minus, e/E) on keyDown for positive-only number inputs.
 */
export const blockNonNumericKeys = (e) => {
  if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault();
};

/**
 * Sanitizes pasted content for positive-only number inputs.
 * Strips non-numeric characters (except decimal point) and clamps to >= 0.
 */
export const sanitizeNumericPaste = (e) => {
  e.preventDefault();
  const pasted = (e.clipboardData || window.clipboardData).getData('text');
  const cleaned = pasted.replace(/[^0-9.]/g, '');
  if (!cleaned) return;
  const num = parseFloat(cleaned);
  if (isNaN(num) || num < 0) return;
  const input = e.target;
  const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
  nativeSetter.call(input, String(num));
  input.dispatchEvent(new Event('input', { bubbles: true }));
};

/**
 * Parses a string value as a positive number.
 * Returns null for empty, the parsed number if valid and >= 0, or undefined if invalid.
 */
export const parsePositiveNumber = (value, parseFunc = parseFloat) => {
  if (value === '') return null;
  const num = parseFunc(value);
  return (!isNaN(num) && num >= 0) ? num : undefined;
};

// "1/4" | "36 1/2"
const FRACTION_PATTERN = /^(?:(\d+(?:\.\d+)?)\s+)?(\d+(?:\.\d+)?)\/(\d+(?:\.\d+)?)$/;

/**
 * Parses a raw numeric input string (decimal or inch fraction) to a number.
 * Returns null for empty/blank, NaN when the text is not a complete number.
 */
export const parseNumericInput = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  const fraction = s.match(FRACTION_PATTERN);
  if (fraction) {
    const denominator = parseFloat(fraction[3]);
    if (!denominator) return NaN;
    return (fraction[1] ? parseFloat(fraction[1]) : 0) + parseFloat(fraction[2]) / denominator;
  }
  if (!/^(\d+\.?\d*|\.\d+)$/.test(s)) return NaN;
  return parseFloat(s);
};

/**
 * Parses a raw integer input string. Returns null for empty, NaN when not a whole number.
 */
export const parseIntegerInput = (raw) => {
  const s = String(raw ?? '').trim();
  if (s === '') return null;
  if (!/^\d+$/.test(s)) return NaN;
  return parseInt(s, 10);
};

/**
 * Validates a completed numeric input value (used on blur and on submit).
 * Returns an error message, or null when the value is acceptable.
 */
export const getNumericFieldError = (
  raw,
  { required = false, integer = false, gtZero = false, min, max, label = 'Value' } = {}
) => {
  const s = String(raw ?? '').trim();
  if (s === '') return required ? `${label} is required` : null;
  const num = integer ? parseIntegerInput(s) : parseNumericInput(s);
  if (num === null || isNaN(num)) {
    return integer ? `${label} must be a whole number` : `Enter a valid ${label.toLowerCase()}`;
  }
  if (gtZero && num <= 0) return `${label} must be greater than 0`;
  if (min !== undefined && num < min) return `${label} must be at least ${min}`;
  if (max !== undefined && num > max) return `${label} must be at most ${max}`;
  return null;
};
